const Joi = require('joi');
const _ = require('lodash');
const { isPointCloud } = require('./point-cloud');
const Transform = require('./transform');

/**
 * @param {PointCloud} pointCloud
 * @param {number[]} i
 * @param {number[]} j
 * @param {number[]} k
 * @returns {Surface}
 */
module.exports.makeSurface = (pointCloud, i, j, k) => {
  Joi.assert(pointCloud, Joi.object().custom(isPointCloud, 'is point cloud'));
  const maxPointCloudIndex = pointCloud.numPoints() - 1;
  Joi.assert(i, Joi.array().items(Joi.number().integer().min(0).max(maxPointCloudIndex)));
  Joi.assert(j, Joi.array().items(Joi.number().integer().min(0).max(maxPointCloudIndex)));
  Joi.assert(k, Joi.array().items(Joi.number().integer().min(0).max(maxPointCloudIndex)));

  if (i.length !== j.length || i.length !== k.length) {
    throw new Error('i, j, and k arrays must all be the same length');
  }

  for (let surfaceIndex = 0; surfaceIndex < i.length; surfaceIndex++) {
    if (i[surfaceIndex] === j[surfaceIndex] || j[surfaceIndex] === k[surfaceIndex] || i[surfaceIndex] === k[surfaceIndex]) {
      throw new Error('duplicate points in triangle');
    }
  }
  return new Surface(pointCloud, _.cloneDeep(i), _.cloneDeep(j), _.cloneDeep(k));
};

/**
 * @param item
 * @returns {boolean}
 */
module.exports.isSurface = (item) => item instanceof Surface;

/**
 * @typedef Surface
 * @property {PointCloud} pointCloud
 */
class Surface {
  constructor(pointCloud, i, j, k) {
    this.pointCloud = pointCloud;
    this._i = i;
    this._j = j;
    this._k = k;
  }

  transform(transform) {
    if (!Transform.isTransform(transform)) {
      throw new Error('transform must be a Transform');
    }
    return new Surface(this.pointCloud.transform(transform), this._i, this._j, this._k);
  }

  /**
   * @returns number
   */
  numTriangles() {
    return this._i.length;
  }

  /**
   * @param index
   * @returns {[[number|null, number|null, number|null]]}
   */
  triangle(index) {
    const points = [this._i[index], this._j[index], this._k[index]];
    return points.map(pointIndex => this.pointCloud.point(pointIndex));
  }

  /**
   * Returns the underlying arrays of points and surface indices.
   * The returned arrays must not be modified.
   * @returns {{x: (number|null)[], y: (number|null)[], z: (number|null)[], i: number[], j: number[], k: number[]}}
   */
  toArrays() {
    return {
      ...this.pointCloud.toArrays(),
      i: this._i,
      j: this._j,
      k: this._k,
    };
  }

  /**
   * Returns true if the surface has no triangles.
   * @returns boolean
   */
  isEmpty() {
    return this._i.length === 0;
  }
}
